import { useState } from 'preact/hooks';
import { BACKPACK_SLOTS, ITEMS, MAX_VAULT_SLOTS, SALVAGE_FAME, vaultSlotCost, type ItemSave } from '@knight/shared';
import * as S from '../app/state';
import { t, tn } from '../i18n';
import { Btn, Icon, HeroPortrait, itemTip, affixText } from './common';
import { api } from '../net/api';
import { heroesView } from '../app/heroes';

function asInst(it: ItemSave, uid: number) {
  return { uid, def: it.def, rarity: it.rarity, affixes: it.affixes ?? [] };
}

function salvageOf(it: ItemSave): number {
  return SALVAGE_FAME[Math.max(0, Math.min(3, ITEMS[it.def]?.legendary ? 3 : it.rarity))] ?? 0;
}

/** The account vault: items rest here safely; heroes carry them in and out through their backpacks. */
export function Vault() {
  const acc = S.account.value;
  const online = S.conn.value === 'online';
  const hv = heroesView();
  const [slot, setSlot] = useState(S.slotChoice.value);
  const [busy, setBusy] = useState(false);
  const save = hv.heroes.find((h) => h.slot === slot) ?? null;
  const vault = acc?.vault ?? [];
  const slots = acc?.vaultSlots ?? 12;

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      const key = (e as { key?: string }).key;
      S.showToast(key && t(key) !== key ? t(key) : t('toast.error', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="screen">
      <div class="card wide panel col tall">
        <div class="row">
          <h2>{t('label.vault')}</h2>
          <span class="small muted">
            {vault.length}/{slots}
          </span>
          <div class="grow" />
          <span class="small muted">
            {t('label.fame')} {acc?.fame ?? 0}
          </span>
          <Btn small onClick={() => (S.screen.value = 'home')}>
            {t('btn.back')}
          </Btn>
        </div>
        <div class="small muted">{t('label.vaultHint')}</div>
        {!online && <div class="small blood">{t('toast.needServer')}</div>}
        <div class="row split" style={{ alignItems: 'flex-start', gap: 20 }}>
          <div class="col" style={{ flex: 1 }}>
            <h3>{t('label.vaultItems')}</h3>
            <div class="list">
              {vault.length === 0 && <div class="item muted small">{t('label.empty')}</div>}
              {vault.map((it, i) => {
                const inst = asInst(it, i);
                return (
                  <div class="item">
                    <Icon kind="item" id={it.def} size="sm" tip={itemTip(inst)} />
                    <div class="col grow" style={{ gap: 0 }}>
                      <b class={'rar' + it.rarity}>{tn('item', it.def)}</b>
                      {(it.affixes ?? []).map((a) => (
                        <span class="small gold">{affixText(a.stat, a.add, a.inc)}</span>
                      ))}
                    </div>
                    <Btn small disabled={busy || !online || !save || save.backpack.length >= BACKPACK_SLOTS} title={save ? undefined : t('label.pickHeroFirst')} onClick={() => void act(async () => void (S.account.value = await api.vaultTake(slot, i)))}>
                      {t('btn.takeToBag')}
                    </Btn>
                    <Btn
                      small
                      ghost
                      disabled={busy || !online}
                      title={t('btn.salvageFor', { fame: salvageOf(it) })}
                      onClick={() => {
                        void S.confirmDialog({ title: t('btn.salvage'), text: t('confirm.salvage', { name: tn('item', it.def), fame: salvageOf(it) }), ok: t('btn.salvage'), danger: true }).then((ok) => {
                          if (ok) void act(async () => void (S.account.value = await api.vaultSalvage(i)));
                        });
                      }}
                    >
                      {t('btn.salvage')} +{salvageOf(it)}
                    </Btn>
                  </div>
                );
              })}
            </div>
            {slots < MAX_VAULT_SLOTS && (
              <Btn small disabled={busy || !online || (acc?.fame ?? 0) < vaultSlotCost(slots)} onClick={() => void act(async () => void (S.account.value = await api.vaultSlotBuy()))}>
                {t('btn.vaultSlots', { cost: vaultSlotCost(slots) })}
              </Btn>
            )}
          </div>
          <div class="col" style={{ flex: 1 }}>
            <h3>{t('label.heroBag')}</h3>
            <div class="row wrap">
              {hv.heroes.map((h) => (
                <div class={'row clickable panel'} style={{ padding: 6, border: '1px solid', borderColor: slot === h.slot ? 'var(--gold)' : 'var(--border)', borderRadius: 4, cursor: 'pointer' }} onClick={() => setSlot(h.slot)}>
                  <HeroPortrait hero={h.heroId} size={24} />
                  <span class="small">
                    {tn('hero', h.heroId)} · {t('label.rankShort')} {h.rank}
                  </span>
                </div>
              ))}
              {hv.heroes.length === 0 && <span class="small muted">{t('label.noHeroes')}</span>}
            </div>
            {save && (
              <>
                <div class="small muted">
                  {t('label.backpack')} {save.backpack.length}/{BACKPACK_SLOTS} · {t('label.equipped')}: {[save.equipment.weapon, save.equipment.armor, save.equipment.charm].filter(Boolean).map((it) => tn('item', it!.def)).join(', ')}
                </div>
                <div class="list">
                  {save.backpack.length === 0 && <div class="item muted small">{t('label.empty')}</div>}
                  {save.backpack.map((it, i) => (
                    <div class="item">
                      <Icon kind="item" id={it.def} size="sm" tip={itemTip(asInst(it, 100 + i))} />
                      <div class="col grow" style={{ gap: 0 }}>
                        <b class={'rar' + it.rarity}>{tn('item', it.def)}</b>
                        {(it.affixes ?? []).map((a) => (
                          <span class="small gold">{affixText(a.stat, a.add, a.inc)}</span>
                        ))}
                      </div>
                      <Btn small disabled={busy || !online || vault.length >= slots} onClick={() => void act(async () => void (S.account.value = await api.vaultStash(slot, i)))}>
                        {t('btn.stash')}
                      </Btn>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
